import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

export type CreatorAnalyticsActiveTab = "overview" | "analytics"

export interface CreatorAnalyticsTabsProps {
  /** Which tab is currently active — drives aria-current + active style. */
  active: CreatorAnalyticsActiveTab
  /** `crewMembershipPath(id)` — the "Overview" tab target. */
  overviewPath: string
  /** `analyticsCrewMembershipPath(id)` — the "Analytics" tab target. */
  analyticsPath: string
  className?: string
}

/**
 * OKS-64 — 2-tab strip rendered on `Brand/Crew/Show.tsx` AND
 * `Brand/Crew/Analytics.tsx`. Tabs are real Inertia `<Link>`s (NOT
 * client-only state) — URL drives selection so deep-linking +
 * browser back-button preserve the active tab (TRD K17).
 *
 * Per UX Spec §5: tab strip remains horizontal on mobile (only 2
 * tabs — fits cleanly at 375px).
 *
 * Active-tab style is a `border-b-2` underline matching the existing
 * shadcn `Tabs` primitive look — but rendered with `<Link>` instead
 * of Radix tabs so navigation is a real route change (not
 * client-state).
 */
export function CreatorAnalyticsTabs({
  active,
  overviewPath,
  analyticsPath,
  className,
}: CreatorAnalyticsTabsProps) {
  const { t } = useTranslation()

  return (
    <nav
      data-testid="creator-analytics-tabs"
      aria-label={t("crew.analytics.tabs.aria_label")}
      className={cn(
        "border-border flex items-center gap-1 border-b",
        className,
      )}
    >
      <TabLink
        href={overviewPath}
        isActive={active === "overview"}
        testId="creator-analytics-tab-overview"
      >
        {t("crew.analytics.tabs.overview")}
      </TabLink>
      <TabLink
        href={analyticsPath}
        isActive={active === "analytics"}
        testId="creator-analytics-tab-analytics"
      >
        {t("crew.analytics.tabs.analytics")}
      </TabLink>
    </nav>
  )
}

interface TabLinkProps {
  href: string
  isActive: boolean
  testId: string
  children: React.ReactNode
}

function TabLink({ href, isActive, testId, children }: TabLinkProps) {
  return (
    <Link
      href={href}
      data-testid={testId}
      aria-current={isActive ? "page" : undefined}
      // Touch target ≥44px (UX Spec §5)
      className={cn(
        "focus-visible:ring-ring -mb-px inline-flex min-h-11 items-center border-b-2 px-4 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
        isActive
          ? "border-foreground text-foreground"
          : "text-muted-foreground hover:text-foreground border-transparent",
      )}
    >
      {children}
    </Link>
  )
}

export default CreatorAnalyticsTabs
